import {
  ScrollEdgeFade,
  useScrollEdges,
} from '@src/components/common/scrollEdgeFade';
import {
  MODAL_TOOLTIP_STYLE,
  PARAGRAPH_TOOLTIP_STYLE,
} from '@src/components/common/tooltipStyles';
import { useListReorderDrag } from '@src/components/common/useListReorderDrag';
import ChevronDownIcon from '@src/svgs/ChevronDownIcon';
import CloseIcon from '@src/svgs/CloseIcon';
import DragHandleIcon from '@src/svgs/DragHandleIcon';
import PlusIcon from '@src/svgs/PlusIcon';
import {
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Tooltip } from 'react-tooltip';
import styled, { css } from 'styled-components';

import LanguageSamplePlayButton from './LanguageSamplePlayButton';
import SettingsAiVoicesLink from './SettingsAiVoicesLink';
import VoiceAvatar from './VoiceAvatar';
import VoiceInfoCard from './VoiceInfoCard';
import { voiceMeta } from './voiceLabels';
import { resolveLanguageDefault } from './voiceLanguageDefaults';
import VoiceLanguagesTooltip from './VoiceLanguagesTooltip';
import { voiceLanguageSummary } from './voiceLanguageSummary';
import { EmptyState, TabBar, TabButton } from './voiceMapperStyles';
import VoiceSelect, { ActionTrigger } from './VoiceSelect';

import type { AiVoiceRecord } from '@src/types.ts/aiVoiceRecord';
import type { Language } from '@src/types.ts/language';

interface SpeakerAIVoiceMapperProps {
  /** The speaker's selected voices, resolved and in priority order (top = default). */
  orderedVoices: AiVoiceRecord[];
  /** Workspace-activated voices not yet selected — the "add a voice" pool. */
  addablePool: AiVoiceRecord[];
  /**
   * Whether the workspace has ANY activated voices. Tells "nothing is activated
   * yet" apart from "every activated voice is already on the speaker" for the
   * add control's copy (PLAT-11567). Inferred from the pool + list when omitted.
   */
  hasActivatedVoices?: boolean;
  /** Workspace supported languages (per-language defaults are set against these). */
  languages: Language[];
  /** Per-language pin map, { [languageId]: aiVoiceId }. */
  languagePrefs: Record<string, string>;
  busy: boolean;
  onAddVoice: (voiceId: string) => void;
  onRemoveVoice: (voiceId: string) => void;
  /** Move a voice in front of `beforeVoiceId`, or last when null (drag or arrow keys). */
  onMoveVoice: (voiceId: string, beforeVoiceId: string | null) => void;
  onSetLanguageDefault: (languageId: string, voiceId: string | null) => void;
  /**
   * The audition panel + voice card, shown as the "Add a voice" step that the
   * Add Voice button opens (PLAT-11581). Without it, Add Voice falls back to a
   * quick-pick menu over `addablePool`.
   */
  auditionArea?: ReactNode;
  /** Names the speaker in the add step's header ("Add a voice to Sean"). */
  speakerName?: string;
  /** The workspace's own name for the intro line ("from those activated for Toro"). */
  workspaceName?: string;
  /**
   * The language the caller came from (PLAT-11581) — the dub modal passes its
   * run language. The modal opens on By Language with this row scrolled into
   * view and highlighted: "fix Sean's Portuguese voice" is a per-language job,
   * and the pin there is the one lever that answers it directly.
   */
  initialLanguageId?: string;
}

// Two facets of one speaker (PLAT-11581): the priority list, or the same voices
// arranged by language. Both belong to the speaker named in the modal header.
type MapperTab = 'voices' | 'languages';
// Within the Voices facet: the list, or the add-a-voice step layered over it.
type MapperView = 'list' | 'add';

// One react-tooltip instance serves every hint on the mapper (row actions, the
// Default note, language lists, the picker's Automatic note).
const MAPPER_TOOLTIP_ID = 'speaker-ai-voice-mapper-tooltip';

// Why Automatic landed on this voice: the top of the order when it speaks the
// language, otherwise the voices above it were skipped for not speaking it.
const automaticHint = (
  caption: string,
  orderedVoices: AiVoiceRecord[],
  resolved: AiVoiceRecord | null,
) => {
  const at = resolved ? orderedVoices.indexOf(resolved) : -1;
  const skipped =
    at > 0
      ? ` The ${at === 1 ? 'voice' : `${at} voices`} above it on Voices ${at === 1 ? "doesn't" : "don't"} speak it.`
      : '';
  return `Picked for you: the first voice in your priority order that speaks ${caption}.${skipped} Choose a voice to pin it instead.`;
};
// The row's language list is rich content (title + stacked names), so it has
// its own tooltip instance in the paragraph style, lifted to the modal's z.
const LANGUAGES_TOOLTIP_ID = 'speaker-ai-voice-mapper-languages-tooltip';
const LANGUAGES_TOOLTIP_STYLE = {
  ...PARAGRAPH_TOOLTIP_STYLE,
  zIndex: MODAL_TOOLTIP_STYLE.zIndex,
};

// The shared per-speaker AI Voice mapper (DE4/DI19), rendered inside the Manage
// AI Voices modal. Three tabs under one coverage line (which workspace
// languages this speaker can be dubbed into today):
//   • Speaker Voices         — the speaker's selected voices in priority order
//                              (top = default); order/remove them and see the
//                              focused voice's card on the right.
//   • Language Voice Defaults — pin a voice per language (drawn from the
//                              speaker's voices) and play its sample in that
//                              language; "Automatic" = the first listed voice
//                              that speaks it.
//   • Find & Audition Voices  — the audition panel + voice card (passed in).
// Every change persists immediately (the modal header says so), so nothing
// here is a form with a Save button.
const SpeakerAIVoiceMapper = ({
  orderedVoices,
  addablePool,
  hasActivatedVoices,
  languages,
  languagePrefs,
  busy,
  onAddVoice,
  onRemoveVoice,
  onMoveVoice,
  onSetLanguageDefault,
  auditionArea,
  speakerName,
  workspaceName,
  initialLanguageId,
}: SpeakerAIVoiceMapperProps) => {
  // PLAT-11581: two speaker-scoped facets instead of three equal tabs. Adding a
  // voice is a step inside the Voices facet, not a destination of its own.
  // By Language leads: "which voice does this speaker get in X" is the question
  // users bring; the priority list explains how the automatic answers arise.
  const [tab, setTab] = useState<MapperTab>('languages');
  const [view, setView] = useState<MapperView>('list');
  // The language the caller arrived for: highlighted, and scrolled into view
  // whenever By Language is opened (the row only exists on that facet).
  const highlightRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (tab === 'languages') {
      highlightRef.current?.scrollIntoView?.({ block: 'center' });
    }
  }, [tab]);
  const topVoice = orderedVoices[0] ?? null;
  const {
    ref: scrollRef,
    edges,
    syncScrollEdges,
  } = useScrollEdges<HTMLDivElement>();

  // The voice whose card shows on the right of the Speaker Voices tab.
  const [selectedVoiceId, setSelectedVoiceId] = useState(topVoice?.id ?? '');
  // Both the drag hook and the arrow keys report the target as a post-move
  // index into these rows — which exclude voices the catalog no longer
  // resolves. Translate it to the voice the moved one should land in front of
  // (null = the end) so no position crosses into the persisted list.
  const anchorFor = (voiceId: string, toIndex: number): string | null =>
    orderedVoices.filter(v => v.id !== voiceId)[toIndex]?.id ?? null;
  const reorderDrag = useListReorderDrag(orderedVoices.length, (from, to) => {
    const voice = orderedVoices[from];
    if (voice) onMoveVoice(voice.id, anchorFor(voice.id, to));
  });
  useEffect(() => {
    if (
      orderedVoices.length > 0 &&
      !orderedVoices.some(v => v.id === selectedVoiceId)
    ) {
      setSelectedVoiceId(orderedVoices[0].id);
    }
  }, [orderedVoices, selectedVoiceId]);
  const selectedVoice =
    orderedVoices.find(v => v.id === selectedVoiceId) ?? null;

  // An empty add pool means one of two very different things; only the caller
  // knows which, so default to "there are voices" unless told otherwise.
  const anyActivated =
    hasActivatedVoices ?? (addablePool.length > 0 || orderedVoices.length > 0);

  // Which workspace languages at least one of the speaker's voices speaks —
  // the same rule the Language Voice Defaults tab resolves with.
  const missingLanguages = useMemo(
    () =>
      languages.filter(
        l =>
          resolveLanguageDefault(orderedVoices, languagePrefs, l).speaking
            .length === 0,
      ),
    [languages, orderedVoices, languagePrefs],
  );

  const renderVoicesTab = () => {
    // Shown under the heading only when there is nothing left to add, so the
    // disabled Add Voice button always comes with its reason.
    const poolNote =
      orderedVoices.length > 0 && addablePool.length === 0 ? (
        anyActivated ? (
          'All activated voices are already on this speaker.'
        ) : (
          <>
            No voices are activated for this workspace yet. Activate some in{' '}
            <SettingsAiVoicesLink />.
          </>
        )
      ) : null;

    return (
      <TabBody>
        <Description>
          {speakerName?.trim()
            ? `${speakerName.trim()}'s voices`
            : "This speaker's voices"}
          , from those activated for {workspaceName?.trim() || 'the workspace'},
          in the order they are tried. Activate more in <SettingsAiVoicesLink />
          .
        </Description>

        <VoicesLayout>
          <ListHeader>
            <div className="row">
              <span className="title">
                <span className="label">Priority Order</span>
                {orderedVoices.length > 0 && (
                  <span className="count">{orderedVoices.length}</span>
                )}
              </span>
              {auditionArea ? (
                <ActionTrigger
                  type="button"
                  onClick={() => setView('add')}
                  disabled={busy || !anyActivated}
                >
                  <PlusIcon
                    width="10"
                    height="10"
                    strokeColor="currentColor"
                    strokeWidth={4}
                  />
                  Add Voice
                </ActionTrigger>
              ) : (
                <VoiceSelect
                  variant="button"
                  buttonLabel="Add Voice"
                  voices={addablePool}
                  value=""
                  onChange={onAddVoice}
                  disabled={busy || addablePool.length === 0}
                />
              )}
            </div>
            <span className="hint">
              For each language, the first voice in this order that speaks it is
              used; a pin on By Language overrides this order for that language.
              Drag to reorder, and select a voice to see its description and the
              languages it speaks.
            </span>
            {poolNote && <span className="hint">{poolNote}</span>}
          </ListHeader>

          <div className="rows">
            {orderedVoices.length === 0 ? (
              <EmptyState>
                {anyActivated ? (
                  'No voices yet. Add one to get started.'
                ) : (
                  <>
                    No voices are activated for this workspace yet. Activate
                    some in <SettingsAiVoicesLink />, then add them here.
                  </>
                )}
              </EmptyState>
            ) : (
              <VoiceList onDragLeave={reorderDrag.handleListDragLeave}>
                {orderedVoices.map((voice, index) => {
                  const summary = voiceLanguageSummary(voice, languages);
                  const moveByKey = (e: KeyboardEvent) => {
                    const to =
                      e.key === 'ArrowUp'
                        ? index - 1
                        : e.key === 'ArrowDown'
                          ? index + 1
                          : null;
                    if (to === null || to < 0 || to >= orderedVoices.length)
                      return;
                    e.preventDefault();
                    onMoveVoice(voice.id, anchorFor(voice.id, to));
                  };
                  return (
                    <VoiceRow
                      key={voice.id}
                      data-voice-row
                      $selected={voice.id === selectedVoiceId}
                      $isDragging={reorderDrag.draggingIndex === index}
                      $dropEdge={reorderDrag.dropEdgeFor(index)}
                      data-drop-edge={
                        reorderDrag.dropEdgeFor(index) ?? undefined
                      }
                      draggable={!busy}
                      onDragStart={e => reorderDrag.handleDragStart(e, index)}
                      onDragOver={e => reorderDrag.handleDragOver(e, index)}
                      onDrop={reorderDrag.handleDrop}
                      onDragEnd={reorderDrag.handleDragEnd}
                    >
                      <HandleColumn>
                        <Position>{index + 1}</Position>
                        <Grip
                          type="button"
                          aria-label={`Reorder ${voice.name}`}
                          data-tooltip-id={MAPPER_TOOLTIP_ID}
                          data-tooltip-content="Drag to reorder"
                          disabled={busy}
                          onKeyDown={moveByKey}
                        >
                          <DragHandleIcon />
                        </Grip>
                      </HandleColumn>
                      <RowSelect
                        type="button"
                        aria-pressed={voice.id === selectedVoiceId}
                        onClick={() => setSelectedVoiceId(voice.id)}
                      >
                        <VoiceAvatar voice={voice} />
                        <span className="labels">
                          <span className="name">{voice.name}</span>
                          <span
                            className="meta"
                            data-tooltip-id={
                              summary.names.length
                                ? LANGUAGES_TOOLTIP_ID
                                : undefined
                            }
                            data-tooltip-content={voice.id}
                          >
                            {voiceMeta(voice.provider, voice.gender)} ·{' '}
                            {summary.text}
                          </span>
                        </span>
                      </RowSelect>
                      <RowActions>
                        <IconButton
                          type="button"
                          className="remove"
                          aria-label={`Remove ${voice.name}`}
                          data-tooltip-id={MAPPER_TOOLTIP_ID}
                          data-tooltip-content="Remove from speaker"
                          disabled={busy}
                          onClick={() => onRemoveVoice(voice.id)}
                        >
                          <CloseIcon width={11} height={11} />
                        </IconButton>
                      </RowActions>
                    </VoiceRow>
                  );
                })}
              </VoiceList>
            )}
          </div>

          {selectedVoice && (
            <CardCell>
              <VoiceInfoCard voice={selectedVoice} variant="detail" />
            </CardCell>
          )}
        </VoicesLayout>
      </TabBody>
    );
  };

  const renderLanguageRows = () => {
    if (orderedVoices.length === 0) {
      return <EmptyState>Add a voice first.</EmptyState>;
    }
    if (languages.length === 0) {
      return (
        <EmptyState>No languages are configured for this workspace.</EmptyState>
      );
    }
    return (
      <LanguageList data-language-list="">
        {languages.map(language => {
          // Only the speaker's voices that actually speak this language are
          // selectable; the ordered-list default resolves to the first such
          // voice. With none, the row says what to do instead of showing a
          // disabled control (shared rule in voiceLanguageDefaults).
          const { speaking, validPinned, resolvedVoiceId } =
            resolveLanguageDefault(orderedVoices, languagePrefs, language);
          const orderedDefault = speaking[0] ?? null;
          return (
            <LanguageRow
              key={language.id}
              ref={language.id === initialLanguageId ? highlightRef : undefined}
              $highlight={language.id === initialLanguageId}
              aria-current={language.id === initialLanguageId || undefined}
            >
              <span className="lang">{language.caption}</span>
              <RowRight>
                {speaking.length === 0 ? (
                  <NoSpeakingVoice>
                    No voice speaks this language yet.{' '}
                    <button
                      type="button"
                      onClick={() => {
                        setTab('voices');
                        setView('add');
                      }}
                      disabled={!auditionArea}
                    >
                      Add a voice
                    </button>
                  </NoSpeakingVoice>
                ) : (
                  <>
                    <SelectCell>
                      <VoiceSelect
                        id={`lang-voice-${language.id}`}
                        voices={speaking}
                        value={resolvedVoiceId}
                        disabled={busy}
                        triggerBadge={validPinned ? 'Pinned' : 'Automatic'}
                        triggerBadgeHint={
                          validPinned
                            ? `You chose this voice for ${language.caption}. It overrides the priority order on Voices. Pick Automatic to follow the order again.`
                            : automaticHint(
                                language.caption,
                                orderedVoices,
                                orderedDefault,
                              )
                        }
                        tooltipId={MAPPER_TOOLTIP_ID}
                        leadingOption={
                          orderedDefault
                            ? {
                                label: 'Automatic',
                                sublabel: `First voice that speaks it · ${orderedDefault.name}`,
                                voice: orderedDefault,
                              }
                            : undefined
                        }
                        onChange={voiceId =>
                          onSetLanguageDefault(language.id, voiceId || null)
                        }
                      />
                    </SelectCell>
                    {resolvedVoiceId && (
                      <LanguageSamplePlayButton
                        aiVoiceId={resolvedVoiceId}
                        language={language.value}
                        disabled={busy}
                        size={24}
                      />
                    )}
                  </>
                )}
              </RowRight>
            </LanguageRow>
          );
        })}
      </LanguageList>
    );
  };

  const renderLanguagesTab = () => (
    <TabBody>
      <Description>
        Choose the voice used when this speaker is dubbed into each language.
        Only voices that speak a language can be chosen; Automatic picks the
        first voice in your list that speaks it.
      </Description>
      {renderLanguageRows()}
    </TabBody>
  );

  // The add step: the audition panel under a header that names the speaker,
  // with the way back to the list. Additions land in the list behind it.
  const renderAddStep = () => (
    <AddStep>
      <StepHeader>
        <BackButton type="button" onClick={() => setView('list')}>
          <span className="chevron" aria-hidden>
            <ChevronDownIcon width="10" height="6" strokeWidth="2" />
          </span>
          Back to voices
        </BackButton>
        <span className="title">
          Add a voice to {speakerName ?? 'this speaker'}
        </span>
      </StepHeader>
      <AuditionBody>{auditionArea}</AuditionBody>
    </AddStep>
  );

  const uncovered = missingLanguages.length;

  return (
    <Root>
      <TabBar role="tablist">
        <TabButton
          type="button"
          role="tab"
          aria-selected={tab === 'languages'}
          $active={tab === 'languages'}
          onClick={() => setTab('languages')}
        >
          By Language
          {uncovered > 0 && (
            // The one thing here that asks for action, visible from the other
            // facet; the hover names the languages.
            <TabNote
              data-tooltip-id={MAPPER_TOOLTIP_ID}
              data-tooltip-content={missingLanguages
                .map(l => l.caption)
                .join(', ')}
            >
              {uncovered} uncovered
            </TabNote>
          )}
        </TabButton>
        <TabButton
          type="button"
          role="tab"
          aria-selected={tab === 'voices'}
          $active={tab === 'voices'}
          onClick={() => setTab('voices')}
        >
          Voices
        </TabButton>
      </TabBar>
      <ScrollRegion>
        <ScrollBody ref={scrollRef} onScroll={syncScrollEdges}>
          {tab === 'languages'
            ? renderLanguagesTab()
            : view === 'list'
              ? renderVoicesTab()
              : renderAddStep()}
        </ScrollBody>
        <SurfaceFade $side="top" $visible={edges.top} />
        <SurfaceFade $side="bottom" $visible={edges.bottom} />
      </ScrollRegion>
      <Tooltip id={MAPPER_TOOLTIP_ID} style={MODAL_TOOLTIP_STYLE} />
      <Tooltip
        id={LANGUAGES_TOOLTIP_ID}
        style={LANGUAGES_TOOLTIP_STYLE}
        opacity={1}
        render={({ content }) => {
          const voice = orderedVoices.find(v => v.id === content);
          return voice ? (
            <VoiceLanguagesTooltip
              names={voiceLanguageSummary(voice, languages).names}
            />
          ) : null;
        }}
      />
    </Root>
  );
};

export default SpeakerAIVoiceMapper;

// Fills the modal's flexible region: a pinned tab bar over a scrolling body so
// a long language list never pushes the modal off-screen.
const Root = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
`;

// The scroll body plus its edge fades: the fades sit beside the scroller (not
// inside it) so they stay pinned to the fold, and are tinted to the modal's
// surface rather than the page background.
const ScrollRegion = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const ScrollBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-top: var(--spacing-lg);
`;

const SurfaceFade = styled(ScrollEdgeFade)`
  && {
    ${p =>
      p.$side === 'top'
        ? css`
            background: linear-gradient(to bottom, var(--surface), transparent);
          `
        : css`
            background: linear-gradient(
              to top,
              var(--surface) 0%,
              var(--surface) 25%,
              transparent 100%
            );
          `}
  }
`;

const TabBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

// The add step fills the scroll body's height so the audition voice card can
// stretch to the bottom (see SpeakerVoiceAuditionArea).
const AddStep = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: 100%;
`;

const AuditionBody = styled.div`
  flex: 1;
  min-height: 0;
`;

// "Back to voices" + the step's title on one row. The chevron points left.
const StepHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;

  .title {
    color: var(--foreground);
    font-size: var(--text-sm);
    font-weight: var(--font-weight-semibold);
  }
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px 4px 6px;
  border: none;
  border-radius: var(--radius-button);
  background: transparent;
  color: var(--text-secondary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition:
    background-color 120ms ease,
    color 120ms ease;

  .chevron {
    display: inline-flex;
    transform: rotate(90deg);
  }
  &:hover {
    background: var(--surface-elevated);
    color: var(--foreground);
  }
`;

// Small muted note beside the "By Language" tab label for the uncovered count.
const TabNote = styled.span`
  margin-left: 8px;
  color: var(--text-muted);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-normal);
  font-variant-numeric: tabular-nums;
`;

// Speaker Voices tab: the voice list on the left, the focused voice's card on
// the right (collapses to one column on a narrow modal).
const VoicesLayout = styled.div`
  display: grid;
  /* The list leads: three fifths to the subject, two to its detail pane. The
     header spans both columns and its hint explains both, so it titles the
     whole tab body; list and pane then hang under it on one line, and the pane
     reads as an annotation of the selected row, not a peer column. */
  grid-template-columns: minmax(0, 3fr) minmax(0, 2fr);
  grid-template-areas:
    'header header'
    'rows detail';
  /* The header (title, action, hint) is one block; the columns under it need a
     full step of air so the hint does not read as the first row's caption. */
  row-gap: 20px;
  column-gap: 24px;
  align-items: start;

  .rows {
    grid-area: rows;
    min-width: 0;
  }

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
    grid-template-areas:
      'header'
      'rows'
      'detail';
  }
`;

const CardCell = styled.div`
  grid-area: detail;
  position: relative;
  min-height: 420px;
  /* Level the anchor line with the first row's name, not its top border. */
  padding-top: 12px;

  @media (max-width: 720px) {
    position: static;
    min-height: 0;
    padding-top: 0;
  }
`;

const Description = styled.p`
  color: var(--text-muted);
  font-size: var(--text-sm);
  line-height: 1.5;
  margin: 0;
`;

// Heading + add action on one row, then the one-line rule, so the order is
// named rather than left to be inferred from the arrows and the list leads
// instead of a full-width picker.
const ListHeader = styled.div`
  grid-area: header;
  display: flex;
  flex-direction: column;
  gap: 4px;

  .row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }
  .title {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }
  .label {
    color: var(--foreground);
    font-size: var(--text-sm);
    font-weight: var(--font-weight-semibold);
  }
  .count {
    padding: 0 8px;
    border-radius: 999px;
    background: var(--surface-elevated);
    color: var(--text-secondary);
    font-size: var(--text-xs);
    font-weight: var(--font-weight-semibold);
    font-variant-numeric: tabular-nums;
  }
  .hint {
    color: var(--text-muted);
    font-size: var(--text-xs);
    line-height: 1.4;
  }
`;

// A bordered table of voice rows (dividers, not separate cards). Each row has
// a grip to reorder and a remove control.
const VoiceList = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid var(--border);
  border-radius: 8px;
  overflow: hidden;
`;

// The focused row (whose card shows on the right) is told apart by a subtle
// surface plus gold text — never a green tint, which is reserved for actions —
// and hover stays the elevated surface so the two states cannot be confused.
//
// Reordering is a drag on the row (the grip marks it, the row carries
// `draggable`, as in the Reel Editor), with an insertion line in the gap the
// drop would land in. Green: the line is where an action lands.
const VoiceRow = styled.div<{
  $selected: boolean;
  $isDragging: boolean;
  $dropEdge: 'above' | 'below' | null;
}>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 8px 8px 8px;
  border-bottom: 1px solid var(--border);
  background: ${p => (p.$selected ? 'var(--surface-subtle)' : 'transparent')};
  opacity: ${p => (p.$isDragging ? 0.4 : 1)};
  box-shadow: ${p =>
    p.$dropEdge === 'above'
      ? 'inset 0 2px 0 var(--primary)'
      : p.$dropEdge === 'below'
        ? 'inset 0 -2px 0 var(--primary)'
        : 'none'};
  transition: var(--transition-normal);

  &:last-child {
    border-bottom: none;
  }
  &:hover {
    background: var(--surface-elevated);
  }

  .name {
    color: ${p => (p.$selected ? 'var(--accent)' : 'var(--foreground)')};
  }
`;

// Position number over the six-dot grip, as on a Reel Editor row. The grip is
// a button so the keyboard can reorder too (arrow keys), and the number is
// what makes "first voice in this order" readable without dragging anything.
const HandleColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  width: 18px;
  flex-shrink: 0;
`;

const Position = styled.span`
  font-size: 11px;
  line-height: 13px;
  font-weight: var(--font-weight-bold);
  color: var(--text-muted);
  font-variant-numeric: tabular-nums;
`;

const Grip = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 2px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: var(--text-muted);
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
  &:focus-visible {
    outline: 2px solid var(--ring);
    outline-offset: 1px;
  }
  &:disabled {
    cursor: default;
    opacity: 0.3;
  }
`;

// The row's selectable part is a real button (keyboard focus, aria-pressed);
// the grip and remove controls are its siblings, never nested inside it.
const RowSelect = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: transparent;
  font-family: var(--font-primary);
  text-align: left;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
  }

  .labels {
    display: flex;
    flex-direction: column;
    gap: 1px;
    min-width: 0;
    flex: 1;
  }
  .name {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: var(--text-sm);
    font-weight: var(--font-weight-semibold);
  }
  .meta {
    color: var(--text-muted);
    font-size: var(--text-xs);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const RowActions = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
`;

// Ghost icon button: quiet at rest, lit on hover, so a five-row list does not
// read as a grid of boxes. Remove only turns destructive on hover.
const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: var(--text-muted);
  line-height: 1;
  cursor: pointer;
  transition: var(--transition-normal);

  &:hover:not(:disabled) {
    background: var(--surface-elevated);
    color: var(--foreground);
  }
  &.remove {
    margin-left: 8px;
  }
  &.remove:hover:not(:disabled) {
    background: color-mix(in srgb, var(--destructive) 14%, transparent);
    color: var(--destructive);
  }
  &:disabled {
    cursor: default;
    opacity: 0.3;
  }
`;

// A bordered table of language rows (dividers, not separate cards) — matching
// the voice tables elsewhere.
const LanguageList = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid var(--border);
  border-radius: 8px;
  overflow: hidden;
`;

// `$highlight`: the language the caller arrived for (dub modal deep link) —
// the same gold-text-on-subtle-surface treatment as the focused voice row.
// The highlight block comes last: its `.lang` rule has the same specificity as
// the base one, so source order is what lets the gold win.
const LanguageRow = styled.div<{ $highlight?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 10px 14px;
  border-bottom: 1px solid var(--border);
  transition: var(--transition-normal);

  &:last-child {
    border-bottom: none;
  }
  &:hover {
    background: var(--surface-elevated);
  }

  /* Regular weight: the row is a label and a value side by side, and neither
     should shout; the highlight (gold) is what singles a row out. */
  .lang {
    color: var(--foreground);
    font-size: var(--text-sm);
    font-weight: var(--font-weight-normal);
  }

  ${p =>
    p.$highlight &&
    css`
      background: var(--surface-subtle);
      .lang {
        color: var(--accent);
      }
    `}
`;

const RowRight = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
`;

// Holds the card-style voice picker at a comfortable width so the stacked
// name/meta never gets cut off (unlike the old single-line native select).
const SelectCell = styled.div`
  width: 360px;
  flex-shrink: 0;
`;

// Replaces the picker on a language none of the speaker's voices can speak:
// says so, and offers the one action that fixes it (green = action).
const NoSpeakingVoice = styled.span`
  color: var(--text-muted);
  font-size: var(--text-xs);
  text-align: right;

  button {
    padding: 0;
    border: none;
    background: none;
    color: var(--primary);
    font: inherit;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }
`;
